import { useEffect, useState } from 'preact/hooks'
import { useAccountLayoutContext } from './AccountProfileContext'
import { Suspense } from './Suspense'
import { FieldDate } from './FieldDate'
import { useFormInput } from '../hooks/useFormInput'
import { FieldText } from './FieldText'
import { FieldSelect } from './FieldSelect'
import LoaderSpin from '@/components/snippets/loader-spin/loader-spin'
import { asyncTimeout } from '../helpers/asyncTimeout'
import { accountTranslations } from '../helpers/translations'
// eslint-disable-next-line camelcase
const transactionData = accountGlobal?.transaction?.modal ?? { body: '', image: '' }

/**
 * @param {{profileId: string}} param0
 * @returns
 */
export const FormClaimTransaction = ({ closeModal }) => {
  const { omneoClient, config, cacheConfig } = useAccountLayoutContext()
  const [onSubmit, setOnSubmit] = useState(false)

  const [submitStatus, setSubmitStatus] = useState({
    message: '',
    type: ''
  })
  const { formInputs, handleOnChange, setDefaults } = useFormInput({
    transactionDate: '',
    transactionReceiptRef: '',
    transactionAmount: 0,
    store: ''
  })
  const [pageData, setPageData] = useState({
    stores: [],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  })

  useEffect(() => {
    if (submitStatus.message?.length) {
      setTimeout(() => {
        setSubmitStatus(() => ({
          message: '',
          type: ''
        }))
      }, 10000)
    }
  }, [submitStatus])

  const handleCreateClaim = async (e) => {
    e.preventDefault()
    setOnSubmit(true)

    setSubmitStatus(() => ({
      message: '',
      type: ''
    }))
    const payload = {
      transaction_transacted_at: formInputs.transactionDate.value,
      transaction_receipt_ref: formInputs.transactionReceiptRef.value,
      transaction_total: formInputs.transactionAmount.value,
      transaction_location_external_code: formInputs.store.value,
      transaction_timezone: pageData.timezone,
      profile_id: config?.customer?.id || cacheConfig?.cacheData?.Profiles?.id || ''
    }

    const transaction = await omneoClient.profileClient.claimTransaction(payload)
    if (transaction?.id) {
      setSubmitStatus(() => ({
        message: 'Transaction Created',
        type: 'success'
      }))
      setDefaults({
        transactionDate: '',
        transactionReceiptRef: '',
        transactionAmount: 0,
        store: ''
      })
      await asyncTimeout(5000)
      setOnSubmit(false)

      return
    }

    setSubmitStatus(() => ({
      message: 'Something went wrong.',
      type: 'error'
    }))
    setOnSubmit(false)
  }

  return (
    <>
      <div className={'h-full overflow-scroll'}>
        <div className={'px-6 py-10'}>
          <p className={'paragraph-md'}>{transactionData.body}</p>
        </div>
        <img className={'w-full p-6 pt-0'} src={transactionData?.image} alt="Transaction Receipt Example" />
        {submitStatus.message.length ? (
          <>
            <span
              className={`px-6 font-bold ${
                submitStatus.type === 'error' ? 'text-primary-sale' : 'text-secondary-green'
              } `}
            >
              {submitStatus.message}
            </span>
          </>
        ) : (
          <></>
        )}
        <div className={'flex flex-col gap-4 px-6 pb-10'}>
          <FieldDate
            required
            name={formInputs.transactionDate.key}
            value={formInputs.transactionDate.value}
            onChange={handleOnChange}
            label={accountTranslations.claim_transaction.form_labels.transaction_date}
          />
          <Suspense
            className="h-full"
            props={{
              asyncCallback: async () => {
                const stores = await omneoClient.profileClient.getStores()
                setPageData((c) => ({ ...c, stores }))
              }
            }}
          >
            <FieldSelect
              required
              name={formInputs.store.key}
              value={formInputs.store.value}
              onChange={handleOnChange}
              placeholder={accountTranslations.claim_transaction.form_placeholders.store_location}
              label={accountTranslations.claim_transaction.form_labels.store_location}
              options={pageData.stores.map((s) => ({ value: s.id, label: s.name }))}
            />
          </Suspense>
          <FieldText
            required
            name={formInputs.transactionReceiptRef.key}
            value={formInputs.transactionReceiptRef.value}
            onChange={handleOnChange}
            placeholder={accountTranslations.claim_transaction.form_placeholders.transaction_receipt_ref}
            label={accountTranslations.claim_transaction.form_labels.transaction_receipt_ref}
          />
          <FieldText
            type="number"
            required
            name={formInputs.transactionAmount.key}
            value={formInputs.transactionAmount.value}
            onChange={handleOnChange}
            label={accountTranslations.claim_transaction.form_labels.transaction_amount}
          />
        </div>
      </div>
      <div className={'flex h-20 w-full gap-4 bg-neutrals-light-grey p-4'}>
        <div onClick={closeModal} className={'btn btn-outline-black btn-md w-fit'}>
          Cancel
        </div>
        <button onClick={handleCreateClaim} className={'btn btn-solid-black btn-md relative w-full overflow-hidden'}>
          {onSubmit ? <LoaderSpin wrapperClass="bg-black" /> : accountTranslations.claim_transaction.cta}
        </button>
      </div>
    </>
  )
}
